import { CreateUserProfile, ProfileDetail, User } from "@garther/interfaces";
import { useMutation, useQuery } from "@tanstack/react-query";

import { useApiClients } from "./context";

export const profileKeys = {
  all: ["profile"] as const,
  byUserId: (userId: string) => [...profileKeys.all, userId] as const
};

export function useProfileByUserId(userId: string, options: { enabled?: boolean } = {}) {
  const { profileApiClient } = useApiClients();

  return useQuery({
    queryKey: profileKeys.byUserId(userId),
    queryFn: () => profileApiClient.getProfileByUserId(userId),
    enabled: (options.enabled ?? true) && !!userId,
    staleTime: 5 * 60 * 1000
  });
}

export function useCreateMyProfileMutation(
  options: { onSuccess?: (result: User) => void; onError?: (error: unknown) => void } = {}
) {
  const { profileApiClient } = useApiClients();
  return useMutation({
    mutationFn: (data: CreateUserProfile) => profileApiClient.create(data),
    onSuccess: (result) => {
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useSetAvatarMutation(
  options: { onSuccess?: (result: ProfileDetail) => void; onError?: (error: unknown) => void } = {}
) {
  const { profileApiClient, publicApiClient } = useApiClients();
  return useMutation({
    mutationFn: async (file: File) => {
      if (!publicApiClient) throw new Error("publicApiClient is not available");
      const { url } = await publicApiClient.uploadImage(file);
      return profileApiClient.setAvatar(url);
    },
    onSuccess: (result) => {
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}
